import { useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PropTypes from 'prop-types';
import { createPdfFromRows } from './pdfExport';
import ExportProgressOverlay from './ExportProgressOverlay';
import ExportResultToast from './ExportResultToast';
import { EXPORT_ORDER, narrowPdfWidths } from '../utils/utils';
import { downloadXlsx } from './xlsxExport';
import axios from 'axios';

const logoPath = `${process.env.PUBLIC_URL}/img/logo_sesc_pdf.png`;

const fmt = iso => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

const parseDateSubtitleFromQS = (qs = '') => {
  const p = new URLSearchParams(qs || '');
  const from = p.get('publishedAtFrom');
  const to = p.get('publishedAtTo');
  const year = p.get('publishedAtYear');
  const month = p.get('publishedAtMonth');
  const preset = p.get('quickPresetLabel');

  let parts = [];
  if (from || to) {
    parts.push(`período ${from || '…'} a ${to || '…'}`);
  } else if (year && month) {
    parts.push(`mês ${String(month).padStart(2, '0')}/${year}`);
  }
  if (preset) parts.push(preset);

  if (!parts.length) {
    const status = p.get('status');
    if (status) parts.push(`status: ${status}`);
  }
  return parts.join(' — ');
};

const makeSubtitle = ({
  publishedAtFrom,
  publishedAtTo,
  quickPresetLabel,
  totalCount,
  fallbackQS,
}) => {
  if (!publishedAtFrom && !publishedAtTo && !quickPresetLabel && fallbackQS) {
    const inferred = parseDateSubtitleFromQS(fallbackQS);
    return inferred
      ? `Filtro: ${inferred}${totalCount != null ? ` — ${totalCount} itens` : ''}`
      : totalCount != null
        ? `${totalCount} itens`
        : '';
  }

  const parts = ['Filtro:'];
  if (quickPresetLabel) parts.push(quickPresetLabel);
  if (publishedAtFrom)
    parts.push(`de ${new Date(publishedAtFrom).toLocaleDateString('pt-BR')}`);
  if (publishedAtTo)
    parts.push(`até ${new Date(publishedAtTo).toLocaleDateString('pt-BR')}`);
  if (totalCount != null) parts.push(`— ${totalCount} itens`);
  return parts.filter(Boolean).join(' ');
};

const normalizeForExport = (items = []) =>
  items.map(r => {
    const out = {};
    EXPORT_ORDER.forEach(({ key, label }) => {
      const v = r?.[key];
      out[label] =
        key === 'createdAt' || key === 'updatedAt' || key === 'publishedAt'
          ? fmt(v)
          : v ?? '';
    });
    return out;
  });

const buildCSV = (headers, rows) => {
  const headerLine = headers.map(h => `"${String(h).replaceAll('"', '""')}"`);
  const body = rows
    .map(r =>
      headers.map(
        h =>
          `"${String(r[h] ?? '')
            .replaceAll('"', '""')
            .replaceAll('\n', ' ')}"`
      )
    )
    .join('\n');
  return `${headerLine}\n${body}`;
};

const downloadCSV = (headers, rows, filename) => {
  const csv = buildCSV(headers, rows);
  const blob = new Blob([`\uFEFF`, csv], { type: 'text/csv;charset=utf-8;' });
  const urlObj = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = urlObj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(urlObj);
};

export default function ExportMenu({
  data,
  listKey = 'videoList',
  fetchPage,
  pageLimit = 50,
  currentQS,
  publishedAtFrom,
  publishedAtTo,
  quickPresetLabel,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [controller, setController] = useState(null);

  const [progress, setProgress] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(null);
  const [etaMs, setEtaMs] = useState(null);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultSeverity, setResultSeverity] = useState('success');
  const [resultTitle, setResultTitle] = useState('');
  const [resultMsg, setResultMsg] = useState('');

  const open = Boolean(anchorEl);
  const handleOpen = e => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const resetProgress = () => {
    setBusy(false);
    setProgress(0);
    setProcessed(0);
    setTotal(null);
    setEtaMs(null);
  };

  const estimateETA = stats => {
    const { pageTimes } = stats;
    if (!pageTimes.length || !total || !processed) return null;
    const avg = pageTimes.reduce((a, b) => a + b, 0) / pageTimes.length;
    const remaining = Math.max(0, total - processed);
    const itemsPerPage = Math.max(1, Math.round(processed / stats.pagesDone));
    const pagesLeft = Math.ceil(remaining / itemsPerPage);
    return Math.round(avg * pagesLeft);
  };

  const collectAll = async () => {
    const ctrl = new AbortController();
    setController(ctrl);
    setBusy(true);

    setProgress(0);
    setProcessed(0);
    setTotal(null);
    setEtaMs(null);

    if (ctrl.signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const pageStats = { pageTimes: [], pagesDone: 0 };

    try {
      let offset = 0;
      const all = [];

      ctrl.signal.throwIfAborted?.();
      let tPage = performance.now();
      const first = await fetchPage({
        offset,
        limit: pageLimit,
        signal: ctrl.signal,
      });
      const tPageEnd = performance.now();
      pageStats.pageTimes.push(tPageEnd - tPage);
      pageStats.pagesDone += 1;

      const grandTotal = Number(
        first.total ?? first.totalItemsCountFiltered ?? 0
      );
      setTotal(grandTotal);

      all.push(...(first.items ?? []));
      setProcessed(all.length);
      setProgress(grandTotal ? (all.length / grandTotal) * 100 : 0);
      setEtaMs(estimateETA(pageStats));

      while (all.length < grandTotal) {
        if (ctrl.signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        offset += pageLimit;
        tPage = performance.now();
        const resp = await fetchPage({
          offset,
          limit: pageLimit,
          signal: ctrl.signal,
        });
        pageStats.pageTimes.push(performance.now() - tPage);
        pageStats.pagesDone += 1;

        all.push(...(resp.items ?? []));
        setProcessed(all.length);
        setProgress((all.length / grandTotal) * 100);
        setEtaMs(estimateETA(pageStats));
      }

      setProgress(100);
      setEtaMs(0);
      return all;
    } catch (err) {
      if (
        err?.name === 'CanceledError' ||
        err?.message === 'aborted by user' ||
        err?.name === 'AbortError'
      ) {
        throw err;
      }
      throw err;
    }
  };

  const notifySuccess = (label, totalCount) => {
    setResultSeverity('success');
    setResultTitle('Exportação concluída');
    setResultMsg(`${label} gerado com ${totalCount} registro(s).`);
    setResultOpen(true);
  };

  const notifyError = (label, error) => {
    setResultSeverity('error');
    setResultTitle('Falha na exportação');
    let reason = '';
    if (error?.response?.status) {
      reason = `HTTP ${error.response.status}`;
    } else if (error?.message) {
      reason = error.message;
    } else {
      reason = 'Erro inesperado.';
    }
    setResultMsg(`Erro ao gerar: ${label}. Motivo: ${reason}`);
    setResultOpen(true);
  };

  const isAbort = err => {
    if (err?.name === 'AbortError') return true;
    if (err?.code === 'ERR_CANCELED') return true;
    if (typeof axios !== 'undefined' && axios.isCancel?.(err)) return true;
    if (
      String(err?.message || '')
        .toLowerCase()
        .includes('aborted')
    )
      return true;
    return false;
  };

  const cancel = () => {
    controller?.abort();
    setController(null);
    resetProgress();
  };

  const exportAllCSV = async () => {
    handleClose();
    try {
      const all = await collectAll();
      const rows = normalizeForExport(all);
      const headers = EXPORT_ORDER.map(o => o.label);
      downloadCSV(headers, rows, 'relatorio_videos.csv');
      notifySuccess('CSV', rows.length);
    } catch (e) {
      if (!isAbort(e)) {
        console.error(e);
        notifyError('o CSV', e);
      }
    } finally {
      resetProgress();
      setController(null);
    }
  };

  const exportAllPDF = async () => {
    handleClose();
    try {
      const all = await collectAll();
      const rows = normalizeForExport(all);
      const subtitle = makeSubtitle({
        publishedAtFrom,
        publishedAtTo,
        quickPresetLabel,
        totalCount: rows.length,
        fallbackQS: currentQS,
      });

      await createPdfFromRows({
        rows,
        EXPORT_ORDER,
        title: 'Relatório de vídeos',
        subtitle,
        filename: 'relatorio_videos.pdf',
        logoPath,
        columnWidths: narrowPdfWidths,
      });
      notifySuccess('PDF', rows.length);
    } catch (e) {
      if (!isAbort(e)) {
        console.error(e);
        notifyError('o PDF', e);
      }
    } finally {
      resetProgress();
      setController(null);
    }
  };

  const exportAllXLSX = async () => {
    handleClose();
    try {
      const all = await collectAll();
      await downloadXlsx({ rows: all, filename: 'relatorio_videos.xlsx' });
      notifySuccess('o XLSX', all.length);
    } catch (e) {
      if (!isAbort(e)) {
        console.error(e);
        notifyError('o XLSX', e);
      }
    } finally {
      resetProgress();
      setController(null);
    }
  };

  return (
    <Box
      onClick={e => e.stopPropagation()}
      sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        size="medium"
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleOpen}>
        Exportar Relatório
      </Button>

      <ExportProgressOverlay
        open={busy}
        progress={progress}
        processed={processed}
        total={total}
        etaMs={etaMs}
        onCancel={cancel}
      />

      <ExportResultToast
        open={resultOpen}
        severity={resultSeverity}
        title={resultTitle}
        message={resultMsg}
        onClose={() => setResultOpen(false)}
      />

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={exportAllCSV}>CSV</MenuItem>
        <MenuItem onClick={exportAllPDF}>PDF</MenuItem>
        <MenuItem onClick={exportAllXLSX}>EXCEL</MenuItem>
      </Menu>
    </Box>
  );
}

ExportMenu.propTypes = {
  data: PropTypes.any,
  listKey: PropTypes.string,
  fetchPage: PropTypes.func.isRequired,
  page: PropTypes.number,
  pageLimit: PropTypes.number,
  pdfTitle: PropTypes.string,
  currentQS: PropTypes.string,
  publishedAtFrom: PropTypes.string,
  publishedAtTo: PropTypes.string,
  quickPresetLabel: PropTypes.string,
};
